// @ts-nocheck
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { getSessionUser } from '@/lib/authSession';

interface Props {
  children: ReactNode;
  /** Shown in the failure card and sent with the alert. */
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** Bumped on retry so the subtree remounts from scratch. */
  resetKey: number;
}

/**
 * Contains a crash to one route/panel: the surrounding shell (sidebar, nav,
 * header) stays alive and Retry remounts only this subtree.
 *
 * It does NOT swallow the error — it still reports to send-error-alert with
 * the same payload shape as the global ErrorBoundary.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null, resetKey: 0 };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`🚨 [ROUTE-ERROR-BOUNDARY] ${this.props.name ?? 'route'}:`, error, errorInfo);
    this.sendErrorAlert(error, errorInfo);
  }

  private async sendErrorAlert(error: Error, errorInfo: ErrorInfo) {
    try {
      const { data: { user } } = await getSessionUser();
      await invokeEdgeFunction('send-error-alert', {
        body: {
          errorType: `Route Error (${this.props.name ?? 'unknown route'})`,
          errorMessage: error.message,
          errorStack: error.stack,
          userEmail: user?.email,
          userId: user?.id,
          context: {
            componentStack: errorInfo.componentStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });
    } catch (alertError) {
      console.error('[ROUTE-ERROR-BOUNDARY] Failed to send error alert:', alertError);
    }
  }

  private handleRetry = () => {
    this.setState((s) => ({ hasError: false, error: null, resetKey: s.resetKey + 1 }));
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-destructive/40 bg-card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-destructive" />
          <h2 className="mb-1 text-lg font-semibold text-foreground">
            {this.props.name ?? 'This page'} hit an error
          </h2>
          <p className="mx-auto mb-5 max-w-md text-sm text-muted-foreground">
            The rest of the app is still running. Your admin team has been notified.
            Retry reloads just this panel.
          </p>
          <Button onClick={this.handleRetry}>Retry</Button>
          {this.state.error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Technical Details
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted p-3 text-xs">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return (
      <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>
    );
  }
}

export default RouteErrorBoundary;
